import Vue from 'vue'
import debounce from 'lodash/debounce'
import { Context } from '@nuxt/types'

export interface ResizeConfigContext extends Context {
  resize: Resize
}

export interface ResizeConfigInject {
  inject: (key: string, value: Resize) => void
}

export enum ResizeType {
  DESKTOP_TYPE = 'desktop',
  TABLET_TYPE = 'tablet',
  MOBILE_TYPE = 'mobile',
}

export interface Resize {
  isDesktop: boolean
  isMobile: boolean
  isTablet: boolean
  windowWidth: number
  device: ResizeType
}

export interface ResizeWatchResizeFn {
  (type: Resize): void
}

const RESIZE_EVENT = 'resize'

const DESKTOP_WIDTH = 1024
const TABLET_WIDTH = 640
const MOBILE_WIDTH = 360
const RESIZING_TIMER_DELAY = 300

const getDeviceType = () => {
  const windowWidth = window.innerWidth
  if (windowWidth >= DESKTOP_WIDTH) {
    return ResizeType.DESKTOP_TYPE
  } else if (windowWidth >= TABLET_WIDTH) {
    return ResizeType.TABLET_TYPE
  } else {
    return ResizeType.MOBILE_TYPE
  }
}

const getInitialDevice = (resize: Resize) => {
  if (resize.isDesktop) {
    return ResizeType.DESKTOP_TYPE
  } else if (resize.isTablet) {
    return ResizeType.TABLET_TYPE
  } else {
    return ResizeType.MOBILE_TYPE
  }
}

const getDeviceWidth = (device: string) => {
  if (device === ResizeType.DESKTOP_TYPE) {
    return DESKTOP_WIDTH
  } else if (device === ResizeType.TABLET_TYPE) {
    return TABLET_WIDTH
  } else {
    return MOBILE_WIDTH
  }
}

export const mapDeviceType = () => {
  const device = getDeviceType()
  const isDesktop = device === ResizeType.DESKTOP_TYPE
  const isTablet = device === ResizeType.TABLET_TYPE
  const isMobile = device === ResizeType.MOBILE_TYPE
  const windowWidth = window.innerWidth

  return {
    device,
    isDesktop,
    isMobile,
    isTablet,
    windowWidth,
  }
}

let currentDeviceType: Resize | null = null
const initResizeListener = (fn: ResizeWatchResizeFn) => {
  return debounce(() => {
    const newType = mapDeviceType()

    if (currentDeviceType !== newType) {
      currentDeviceType = newType
      fn(newType)
    }
  }, RESIZING_TIMER_DELAY)
}

export const watchResize = (fn: ResizeWatchResizeFn) => {
  return window.addEventListener(RESIZE_EVENT, initResizeListener(fn), {
    passive: true,
  })
}

const strategies = {
  client(ctx: ResizeConfigContext) {
    const globalData = new Vue({
      data: {
        $device: ctx.nuxtState.resize,
      },
    })

    Vue.mixin({
      computed: {
        $resize: {
          get() {
            return globalData.$data.$device
          },
          set(newColor) {
            globalData.$data.$device = newColor
          },
        },
      },
    })
  },

  server(ctx: ResizeConfigContext, inject: ResizeConfigInject['inject']) {
    const { isDesktop, isMobile, isTablet } = ctx.app.$device
    const device = getInitialDevice(ctx.app.$device)
    const windowWidth = getDeviceWidth(device)
    const resize = {
      device,
      isDesktop,
      isMobile,
      isTablet,
      windowWidth,
    }

    inject('resize', {
      ...resize,
    })

    ctx.beforeNuxtRender(({ nuxtState }) => {
      nuxtState.resize = {
        ...resize,
      }
    })
  },
}

export default (
  ctx: ResizeConfigContext,
  inject: ResizeConfigInject['inject']
) => {
  const key = process.client ? 'client' : 'server'
  const strategy = strategies[key]
  return strategy(ctx, inject)
}
